import { createContext, type ReactNode } from "react";

/**
 * A page drawn for looking only (the Appearance editor's demo record, #13370): its doorways are
 * text and its record references are names, so nothing in it acts or opens.
 */
export const StillPageContext = createContext(false);

/**
 * Where an editor's layer draws a card's edit button (Appearance, #13370 round 36; in the card
 * header's action slot since round 39): while true, the Overview's Key facts and Documents cards
 * mark the right end of their header's action slot with `data-page-anchor="card-edit"` (and
 * `data-anchor-key` the card), a mark that takes no room, and the layer places its "Edit facts" /
 * "Edit columns" button there, right-aligned on the title's line. False everywhere else, so a
 * record page draws no mark.
 */
export const CardEditAnchorContext = createContext(false);

/** The mark at the right end of a card header's action slot its edit button is drawn at. */
export const CARD_EDIT_ANCHOR = "card-edit";
/** The edit button's height. */
export const CARD_EDIT_BUTTON_PX = 26;

/**
 * The places on the Documents tab an editor's layer draws its controls over (round 36b), each a
 * `data-page-anchor` the tab marks always (an attribute, nothing drawn): a grid header cell
 * (`data-anchor-key` its column, "name" for the name column), a List group row (its group) and
 * the Table | List toggle (`data-view-mode` the view it shows).
 */
export const COLUMN_ANCHOR = "column";
export const GROUP_ANCHOR = "group";
export const VIEW_MODE_ANCHOR = "view-mode";
/**
 * A List group's column header cell (#13370, round 37), marked on a still page only:
 * `data-anchor-key` its column ("filename" for the name column), `data-anchor-row` its group.
 */
export const LIST_COLUMN_ANCHOR = "list-column";
/**
 * An editor's tools for a grid header cell (#13370, round 37, item 12; Gerry, 20:06 PT: "when you
 * scroll down on the table view for Documents it doesnt act like the table"): given a column, what
 * the header cell draws inside itself, laid out and scrolled with the cell, so the tools never
 * lag the header. Null everywhere but the Appearance editor's preview.
 */
export const HeaderToolsContext = createContext<
  ((column: string) => ReactNode) | null
>(null);

/**
 * An editor's tool drawn at the end of a grid's header row (Appearance, #13370 round 39: the dashed
 * "+" that adds a column), in place of the grid's own column selector. Null everywhere but the
 * Appearance editor's preview.
 */
export const HeaderAddToolContext = createContext<ReactNode>(null);

/**
 * An editor's own column picker behind a collection toolbar's "Columns" (Appearance, #13370 round
 * 39): given, the toolbar's Columns button stays live in the still page and opens the editor's
 * picker on itself instead of the grid's own menu. Null everywhere but the Appearance editor.
 */
export const ColumnsToolContext = createContext<
  ((anchor: HTMLElement) => void) | null
>(null);

/**
 * An editor's line drawn under a collection's toolbar (Appearance, #13370 round 39: the List's
 * "Grouped by" line). Null everywhere but the Appearance editor's preview.
 */
export const ToolbarFootContext = createContext<ReactNode>(null);

/**
 * Marks an editor's own tool drawn inside a still page (the Appearance editor's preview), which
 * stays live while the page around it is inert (`ScrollablePage`).
 */
export const LAYER_TOOL_ATTRIBUTE = "data-layer-tool";

/** A column header's key for the grid's name column. */
export const NAME_COLUMN_ANCHOR_KEY = "name";

/**
 * Whether the Holdings tab draws the row of totals switches under its totals row (#14991, section
 * E). True only where the Appearance editor mounts the layer that draws the switches live over
 * that row (the native Holdings tab's sample); false everywhere else, a saved holdings view tab's
 * sample included, so no row of controls is drawn without its controls.
 */
export const HoldingsTotalsControlsContext = createContext(false);
